// MenuPage.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import CategoryList from "./CategoryList";
import ProductCard from "./ProductCard";
import { Category, Product } from "./types";
import { useMenu } from "../../contexts/MenuContext";
import { useNavigate } from "react-router-dom";
import "./MenuPage.css"; // stili generali
import { useTenant } from "../../contexts/TenantContext";

const MenuPage: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const tenantCtx = useTenant();
    const { tenantId, loading, error, menuData, reloadData, features } = tenantCtx as any;
    const categories = (tenantCtx as any).categories as { id: string; name: string; image?: string }[] || [];
    const subcategories = (tenantCtx as any).subcategories as { id: string; name: string; image?: string; categoryId: string }[] || [];


    const [selectedCategory, setSelectedCategory] = useState<string>("");
    const [selectedSubcategory, setSelectedSubcategory] = useState<string>("");
    const [searchTerm, setSearchTerm] = useState<string>("");
    const menu = useMenu();
    if (!menu) return <div>DEBUG: MenuPage attivo</div>;
    const { cart, addToCart, decrementItem, clearCart, activeOrder } = menu;
    const canOrderDineIn = Boolean(features?.orderingEnabled);
    const canOrderDelivery = Boolean(features?.deliveryEnabled);
    const canAddToCart = canOrderDineIn || canOrderDelivery;


    useEffect(() => {
        if (tenantId && !loading && !error) {
            if (categories && categories.length > 0) {
                setSelectedCategory(prev => prev || categories[0].id);
                setSelectedSubcategory("");
            } else {
                // No categories: show all products
                setSelectedCategory("");
                setSelectedSubcategory("");
            }
        }
    }, [tenantId, loading, error, categories]);

    // Evita loop di reload quando il menu è legittimamente vuoto
    const triedInitialLoad = useRef(false);
    useEffect(() => {
        if (!tenantId) return;
        if (loading || error) return;
        if (triedInitialLoad.current) return;
        if (!Array.isArray(menuData) || menuData.length === 0) {
            triedInitialLoad.current = true; // solo una volta per mount
            reloadData();
        }
    }, [tenantId, loading, error, menuData, reloadData]);

    // Le categorie arrivano già tradotte dal backend
    const uiCategories: Category[] = useMemo(() => {
        const list = Array.isArray(categories) ? categories : [];
        return list.map((c: { id: string; name: string; image?: string }) => ({
            id: c.id,
            name: c.name, // Già tradotto dal backend
            image: c.image
        }));
    }, [categories]);    // I prodotti arrivano già tradotti dal backend
    const products: Product[] = useMemo(() => {
        const subMap = new Map<string, string>();
        (subcategories || []).forEach(s => subMap.set(s.id, s.categoryId));
        return (menuData || []).map((it: any, idx: number) => {
            const subId = it.sottocategoria || undefined;
            let catId = it.categoria || "";
            if (!catId && subId) {
                const derived = subMap.get(subId);
                if (derived) catId = derived;
            }
            const productId = it.id ? String(it.id) : `${idx}-${it.nome}`;
            const variants = Array.isArray(it.variants)
                ? it.variants
                    .filter((variant: any) => variant && variant.available !== false)
                    .map((variant: any) => ({
                        id: String(variant.id),
                        name: variant.name,
                        price: Number(variant.price ?? 0),
                        available: variant.available !== false,
                    }))
                : undefined;
            return {
                id: productId,
                name: it.nome, // Già tradotto dal backend
                description: it.descrizione || undefined, // Già tradotto dal backend
                price: it.prezzo,
                categoryId: catId,
                subcategoryId: subId,
                image: it.immagine,
                variants
            } as Product;
        });
    }, [menuData, subcategories]);

    useEffect(() => {
        if (!selectedSubcategory) return;
        const belongsToCategory = (subcategories || []).some(sub => sub.id === selectedSubcategory && sub.categoryId === selectedCategory);
        if (!belongsToCategory) {
            setSelectedSubcategory("");
        }
    }, [selectedCategory, selectedSubcategory, subcategories]);

    const handleSelectCategory = (id: string) => {
        setSelectedCategory(id);
        setSelectedSubcategory("");
    };

    const cartMap = useMemo(() => {
        const map = new Map<string, number>();
        cart.forEach(item => {
            map.set(item.productId, item.quantity);
        });
        return map;
    }, [cart]);

    const cartKeyFor = (productId: string, variantId?: string) => variantId ? `${productId}__v_${variantId}` : productId;

    const filteredProducts = useMemo(() => {
        // Se c'è un termine di ricerca, mostra tutti i prodotti che corrispondono
        if (searchTerm.trim()) {
            const searchLower = searchTerm.toLowerCase().trim();
            return products.filter(product =>
                product.name.toLowerCase().includes(searchLower) ||
                (product.description && product.description.toLowerCase().includes(searchLower))
            );
        }

        // Altrimenti, usa la logica normale di categoria/sottocategoria
        let list = selectedCategory ? products.filter(p => p.categoryId === selectedCategory) : products;
        if (selectedCategory && selectedSubcategory) {
            list = list.filter(p => p.subcategoryId === selectedSubcategory);
        }
        return list;
    }, [products, selectedCategory, selectedSubcategory, searchTerm]);

    const total = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);

    const selectedCategoryObj = uiCategories.find(cat => cat.id === selectedCategory);
    const uiSubcategories = useMemo(() =>
        subcategories
            .filter(s => s.categoryId === selectedCategory)
            .map(s => ({
                ...s,
                name: s.name // Già tradotto dal backend
            })),
        [subcategories, selectedCategory]
    );
    const selectedSubObj = uiSubcategories.find(s => s.id === selectedSubcategory);

    if (!tenantId) {
        return (
            <div className="menu-page" style={{ padding: '2rem' }}>
                <div className="menu-header-space">
                    <div className="menu-back-btn-row">
                        <button className="menu-back-btn" onClick={() => navigate('/')}>{t('menu.back')}</button>
                    </div>
                    <div className="menu-search-row">
                        <input
                            className="menu-search-input"
                            placeholder={t('menu.searchPlaceholder')}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>
                <div className="product-list">
                    {[...Array(3)].map((_, i) => (
                        <div key={i} className="card articolo-card" style={{ width: 380, height: 120, borderRadius: 12, background: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }} />
                    ))}
                </div>
            </div>
        );
    }

    if (loading) return (
        <div className="menu-page" style={{ padding: '2rem' }}>
            <div className="menu-header-space">
                <div className="menu-back-btn-row">
                    <button className="menu-back-btn" onClick={() => navigate('/')}>{t('menu.back')}</button>
                </div>
                <div className="menu-search-row">
                    <input
                        className="menu-search-input"
                        placeholder={t('menu.searchPlaceholder')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>
            <div className="product-list">
                {[...Array(5)].map((_, i) => (
                    <div key={i} className="card articolo-card" style={{ width: 380, height: 120, borderRadius: 12, background: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }} />
                ))}
            </div>
        </div>
    );
    if (error) return (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#e91e63' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>
                {/* Emoji di errore carina */}
                <span role="img" aria-label="Errore">😕</span>
            </div>
            <p style={{ fontWeight: 600, fontSize: 20 }}>
                {t('menu.errorLoading') || "Si è verificato un errore nel caricamento del menu."}
            </p>
            <p style={{ color: '#666', fontSize: '15px', marginTop: '10px' }}>
                {t('menu.tryAgainLater') || "Riprova più tardi oppure aggiorna la pagina."}
            </p>
            <button
                onClick={reloadData}
                style={{
                    padding: '0.5rem 1rem',
                    background: '#f5f5f5',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    marginTop: '20px'
                }}>
                {t('menu.retry') || "Riprova"}
            </button>
        </div>
    ); return (
        <div className="menu-page">
            <div className="menu-header-space">
                <div className="menu-back-btn-row">
                    <button className="menu-back-btn" onClick={() => navigate("/")}>{t('menu.back')}</button>
                </div>
                <CategoryList
                    categories={uiCategories}
                    onSelect={handleSelectCategory}
                    selectedCategory={selectedCategory}
                />
                {/* Subcategory horizontal bar (appears only if category has subs) */}
                {uiSubcategories.length > 0 && (
                    <div className="category-bar">
                        {uiSubcategories.map(sub => (
                            <button
                                key={sub.id}
                                className={`category-pill${selectedSubcategory === sub.id ? " active" : ""}`}
                                onClick={() => setSelectedSubcategory(prev => prev === sub.id ? "" : sub.id)}
                            >
                                {sub.image && (
                                    <img
                                        src={sub.image}
                                        alt=""
                                        className="category-icon"
                                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                        style={{ width: 24, height: 24, marginRight: 5, borderRadius: '50%', objectFit: 'cover' }}
                                    />
                                )}
                                {sub.name}
                            </button>
                        ))}
                    </div>
                )}
                <div className="menu-search-row">
                    <input
                        className="menu-search-input"
                        placeholder={t('menu.searchPlaceholder')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>
            {(selectedSubObj?.image || selectedCategoryObj?.image) && (
                <div className="category-image-row">
                    <img
                        src={selectedSubObj?.image || selectedCategoryObj?.image || ''}
                        alt={selectedSubObj?.name || selectedCategoryObj?.name || ''}
                        className="category-image"
                        onError={(e) => {
                            e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMjAiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZpbGw9IiNhYWFhYWEiPkltbWFnaW5lIGNhdGVnb3JpYSBub24gZGlzcG9uaWJpbGU8L3RleHQ+PC9zdmc+';
                            e.currentTarget.onerror = null; // Prevent infinite loops
                        }}
                    />
                </div>
            )}
            <div className="product-list">
                {filteredProducts.map(prod => (
                    <ProductCard
                        key={prod.id}
                        product={prod}
                        onAdd={(product, variant) => {
                            if (!canAddToCart) return;
                            if (variant) {
                                const baseQuantity = cartMap.get(cartKeyFor(product.id)) || 0;
                                if (baseQuantity === 0) {
                                    return;
                                }
                            }
                            const key = cartKeyFor(product.id, variant?.id);
                            addToCart({
                                productId: key,
                                baseProductId: product.id,
                                variantId: variant?.id,
                                name: product.name,
                                variantName: variant?.name,
                                price: variant ? variant.price : product.price,
                                quantity: 1,
                            });
                        }}
                        onRemove={canAddToCart ? (product, variant) => decrementItem(cartKeyFor(product.id, variant?.id)) : undefined}
                        baseQuantity={cartMap.get(cartKeyFor(prod.id)) || 0}
                        variantQuantities={prod.variants?.reduce<Record<string, number>>((acc, variant) => {
                            const variantId = String(variant.id);
                            acc[variantId] = cartMap.get(cartKeyFor(prod.id, variant.id)) || 0;
                            return acc;
                        }, {}) || {}}
                        orderingEnabled={canAddToCart}
                    />
                ))}
                {filteredProducts.length === 0 && (
                    <div style={{ padding: '1rem', textAlign: 'center', color: '#666' }}>
                        {t('menu.noProductsCategory')}
                    </div>
                )}
            </div>
            {canAddToCart && (
                <div className="cart-footer">
                    <div className="cart-footer__info">
                        <span className="cart-total">{t('menu.total')}: €{total.toFixed(2)}</span>
                        <span className="cart-count">{t('order.itemsCount', { count: totalItems })}</span>
                        {activeOrder?.orderCode && (
                            <span className="cart-active-order">
                                {t('order.activeCodeLabel', { code: activeOrder.orderCode })}
                            </span>
                        )}
                    </div>
                    <div className="cart-footer__actions">
                        <button
                            className="cart-secondary-btn"
                            type="button"
                            onClick={clearCart}
                            disabled={cart.length === 0}
                        >
                            {t('order.clearCart')}
                        </button>
                        {canOrderDineIn && (
                            <button
                                className="cart-pay-btn"
                                type="button"
                                onClick={() => navigate('/orders')}
                                disabled={cart.length === 0 && !activeOrder}
                            >
                                {t('order.goToOrders')}
                            </button>
                        )}
                        {canOrderDelivery && (
                            <button
                                className="cart-pay-btn"
                                type="button"
                                onClick={() => navigate('/delivery')}
                                disabled={cart.length === 0}
                            >
                                {t('delivery.goToDelivery')}
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default MenuPage;
